import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { BaseFieldComponent } from '../../base-field/base-field.component';
import { FieldBase, FieldOption } from '@nagel-types';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AutocompleteService } from '../autocompleteService/autocomplete.service';
import { IconComponent } from '@nagel-assests';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HighlightTextDirective } from '@nagel-directives';
import { FILTER_OPERATORS } from '../../../formConst';

export type MultiselectAutocompleteResponseType = {
    records: string[];
    totalRecordsCount: number;
};

@Component({
    selector: 'lib-multiselect-autocomplete',
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatChipsModule,
        MatInputModule,
        IconComponent,
        MatDividerModule,
        MatProgressSpinner,
        HighlightTextDirective
    ],
    providers: [AutocompleteService],
    templateUrl: './multiselectAutocomplete.component.html',
    styleUrl: './multiselectAutocomplete.component.scss'
})
export class MultiselectAutocompleteComponent extends BaseFieldComponent implements OnInit {
    @ViewChild('input') input!: ElementRef<HTMLInputElement>;
    @Input() override field!: FieldBase<FieldOption[]>;
    @Input() override form!: FormGroup;

    options: FieldOption[] = [];
    isLoading = false;
    selectedOptions: FieldOption[] = [];

    constructor(
        private autocompleteService: AutocompleteService
    ) {
        super();
        this.autocompleteService.optionsObservable$
            .subscribe((options: FieldOption[]) => {
                this.options = options
                    .filter((option: FieldOption) => {
                        return !this.selectedOptions.some((selectedOption: FieldOption) => {
                            return selectedOption.value === option.value;
                        });
                    })
                    .map((option: FieldOption) => {
                        return { viewValue: option.viewValue, value: option.value };
                    });
            });
    }

    ngOnInit(): void {
        this.selectedOptions = this.field.value || [];
    }

    shouldShowLoadingSpinner(): boolean {
        return this.autocompleteService.isLoading$.value;
    }

    handleOnAutocompleteOpen = () => {
        this.makeFilterRequest();
    };

    private makeFilterRequest = () => {
        this.autocompleteService.shouldFireRequest$.next({
            url: this.field.url || '',
            filterCriterias: [
                {
                    fieldName: this.field.key,
                    value: this.input.nativeElement.value,
                    filterOperator: FILTER_OPERATORS.CONTAINS_IGNORE_CASE,
                    isCalc: false
                }
            ],
            responseMapperFn: this.mapResponseToOptions
        });
    };

    private mapResponseToOptions = (response: MultiselectAutocompleteResponseType): FieldOption[] => {
        return response.records.map((record: string) => ({
            viewValue: record,
            value: record
        }));
    };

    onBlur() {
        const searchTerm = this.input.nativeElement.value;
        const potentialMatches = this._filter(this.options, searchTerm || '');

        if (potentialMatches.length && searchTerm === potentialMatches[0].viewValue) {
            const valueToApply = potentialMatches[0];
            this.selectedOptions.push(valueToApply);
            this.form.controls[this.field.key].setValue(valueToApply);
        }
    }

    addSelectedOption(option: FieldOption) {
        this.selectedOptions.push(option);

        if (this.field.onChange) {
            this.field.onChange({ value: this.selectedOptions, key: this.field.key });
        }
        this.input.nativeElement.value = '';
        this.makeFilterRequest();
    }

    removeSelectedOption(option: FieldOption) {
        this.selectedOptions = this.selectedOptions.filter(selectedOption => selectedOption.value !== option.value);

        if (this.field.onChange) {
            this.field.onChange({ value: this.selectedOptions, key: this.field.key });
        }

        this.input.nativeElement.value = '';
        this.makeFilterRequest();
    }

    onInputChange() {
        this.makeFilterRequest();
    }

    private _filter(options: FieldOption[], searchTerm: string): FieldOption[] {
        const filterValue = searchTerm.toLowerCase();

        return options.filter(option => option.viewValue.toLowerCase().includes(filterValue));
    }

    getOptionIcons = (iconString: string | string[] | undefined): string[] => {
        let result: string[] = [];

        if (iconString) {
            result = Array.isArray(iconString)
                ? iconString
                : [iconString];
        }

        return result;
    };

    displayWith = (option: FieldOption): string => {
        return option?.viewValue || '';
    };
}